import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Plus, Save, X, Eye, Edit2, Trash2, Globe } from 'lucide-react';
import { useAdminT } from '@/lib/adminTranslations';
import { sanitizeHtml } from '@/lib/sanitize';

const API_BASE = '';
const LANGS = ['en', 'tr', 'es', 'pt', 'de', 'fr', 'it', 'nl', 'ru', 'uk', 'ar', 'id'];
const PAGE_TYPES = ['corporate', 'legal', 'seo_content', 'affiliate', 'guide'];

export default function AdminPagesManager() {
  const t = useAdminT();
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [filterLang, setFilterLang] = useState('en');
  const [filterType, setFilterType] = useState('');

  const [form, setForm] = useState({
    slug: '', language: 'en', page_type: 'corporate', status: 'published',
    title: '', meta_title: '', meta_description: '', og_title: '', og_description: '',
    content_html: '', is_indexable: true, show_in_footer: true, show_in_header: false,
    faq_items: '[]',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ language: filterLang });
      if (filterType) params.append('page_type', filterType);
      const res = await fetch(`${API_BASE}/api/admin/pages?${params}`, { headers });
      const data = await res.json();
      setPages(data.pages || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filterLang, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (p) => {
    setEditing(p.slug + ':' + p.language);
    setForm({
      ...p,
      faq_items: JSON.stringify(p.faq_items || [], null, 2),
    });
    setPreview(null);
  };

  const startCreate = () => {
    setEditing('__new__');
    setForm({
      slug: '', language: filterLang, page_type: 'corporate', status: 'published',
      title: '', meta_title: '', meta_description: '', og_title: '', og_description: '',
      content_html: '', is_indexable: true, show_in_footer: true, show_in_header: false,
      faq_items: '[]',
    });
  };

  const handleSave = async () => {
    let faqItems = [];
    try { faqItems = JSON.parse(form.faq_items || '[]'); } catch { faqItems = []; }
    const body = { ...form, faq_items: faqItems };
    delete body._id;
    await fetch(`${API_BASE}/api/admin/pages`, { method: 'POST', headers, body: JSON.stringify(body) });
    setEditing(null);
    fetchData();
  };

  const handleDelete = async (slug, language) => {
    if (!window.confirm(`Delete ${slug} (${language})?`)) return;
    await fetch(`${API_BASE}/api/admin/pages/${slug}?language=${language}`, { method: 'DELETE', headers });
    fetchData();
  };

  if (loading) return <div className="p-6 text-slate-400">{t.loading}</div>;

  const typeColors = { corporate: 'border-blue-500/30 text-blue-400', legal: 'border-amber-500/30 text-amber-400', seo_content: 'border-emerald-500/30 text-emerald-400', affiliate: 'border-violet-500/30 text-violet-400', guide: 'border-cyan-500/30 text-cyan-400' };

  return (
    <div className="space-y-6" data-testid="admin-pages-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100" style={{ fontFamily: 'var(--font-heading)' }}>
          <FileText className="w-6 h-6 inline mr-2 text-blue-400" /> {t.pagesManager}
        </h1>
        <Button size="sm" onClick={startCreate} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="create-page-btn">
          <Plus className="w-4 h-4 mr-1" /> {t.newPage}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select value={filterLang} onChange={e => setFilterLang(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300">
          {LANGS.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300">
          <option value="">All Types</option>
          {PAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Editor */}
      {editing && (
        <div className="bg-slate-900/80 border border-blue-500/30 rounded-xl p-5 space-y-4" data-testid="page-editor">
          <h2 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">
            {editing === '__new__' ? t.createPage : t.editPage}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input placeholder="slug (e.g. about)" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200" disabled={editing !== '__new__'} />
            <select value={form.language} onChange={e => setForm({...form, language: e.target.value})}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
              {LANGS.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
            <select value={form.page_type} onChange={e => setForm({...form, page_type: e.target.value})}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
              {PAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          <input placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Meta Title" value={form.meta_title} onChange={e => setForm({...form, meta_title: e.target.value})}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-300" />
            <input placeholder="Meta Description" value={form.meta_description} onChange={e => setForm({...form, meta_description: e.target.value})}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-300" />
            <input placeholder="OG Title" value={form.og_title || ''} onChange={e => setForm({...form, og_title: e.target.value})}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-300" />
            <input placeholder="OG Description" value={form.og_description || ''} onChange={e => setForm({...form, og_description: e.target.value})}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-300" />
          </div>
          <textarea placeholder="Content HTML" value={form.content_html} onChange={e => setForm({...form, content_html: e.target.value})}
            rows={10} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 font-mono" data-testid="content-editor" />
          <textarea placeholder='FAQ Items JSON: [{"question":"...","answer":"..."}]' value={form.faq_items}
            onChange={e => setForm({...form, faq_items: e.target.value})} rows={3}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 font-mono" />
          <div className="flex gap-4 text-xs text-slate-400">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.is_indexable} onChange={e => setForm({...form, is_indexable: e.target.checked})} /> Indexable</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.show_in_footer} onChange={e => setForm({...form, show_in_footer: e.target.checked})} /> Footer</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.show_in_header} onChange={e => setForm({...form, show_in_header: e.target.checked})} /> Header</label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="save-page-btn">
              <Save className="w-4 h-4 mr-1" /> {t.save}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPreview(form.content_html)} className="text-slate-400">
              <Eye className="w-4 h-4 mr-1" /> {t.preview}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setPreview(null); }} className="text-slate-400">
              <X className="w-4 h-4 mr-1" /> {t.cancel}
            </Button>
          </div>
          {preview && (
            <div className="border border-slate-700 rounded-lg p-4 mt-3 bg-slate-950" data-testid="page-preview">
              <div className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Preview</div>
              <div className="prose prose-invert prose-slate prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview) }} />
            </div>
          )}
        </div>
      )}

      {/* Pages List */}
      <div className="space-y-2">
        {pages.length === 0 ? (
          <div className="text-center text-slate-500 py-8">No pages found.</div>
        ) : pages.map((p, i) => (
          <div key={i} className="flex items-center justify-between bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-3" data-testid={`page-item-${p.slug}`}>
            <div className="flex items-center gap-2 min-w-0">
              <Badge variant="outline" className={typeColors[p.page_type] || 'border-slate-700 text-slate-400'}>{p.page_type}</Badge>
              <span className="text-sm font-mono text-slate-300 truncate">/{p.slug}</span>
              <span className="text-xs text-slate-500">{p.title}</span>
              {p.status === 'draft' && <Badge variant="outline" className="border-amber-500/30 text-amber-400">Draft</Badge>}
              {!p.is_indexable && <Badge variant="outline" className="border-red-500/30 text-red-400">noindex</Badge>}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <a href={`/${filterLang}/${p.slug}`} target="_blank" rel="noopener noreferrer"
                className="text-slate-500 hover:text-emerald-400 p-1"><Globe className="w-4 h-4" /></a>
              <button onClick={() => startEdit(p)} className="text-slate-500 hover:text-blue-400 p-1"><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(p.slug, p.language)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
